import { COLUMN_SELECT_ID } from "@/constants";
import { Row, Table } from "@tanstack/react-table";

/**
 * Finds the column that gets wrapped in a link by DataTableCellContent.
 * Mirrors the logic in List.tsx: first visible non-select column
 * whose meta doesn't set allowColumnToBeALink to false.
 */
function findLinkColumnId<TData>(table: Table<TData>): string | undefined {
  const visibleHeaders = table.getLeafHeaders();
  const firstDataColumn = visibleHeaders.find(
    (header) =>
      header.column.id !== COLUMN_SELECT_ID &&
      header.column.columnDef.meta?.allowColumnToBeALink !== false,
  );
  return firstDataColumn?.column.id;
}

export type LinkResolver<TData> = (
  row: Row<TData>,
  columnId: string,
) => string | null;

/**
 * Creates a resolver for the pathBuilder link that DataTableCellContent
 * adds to the first visible column. This wrapping happens outside the
 * column's own cell renderer, so getCellHtmlValue can't detect it —
 * the resolver is passed as a fallback.
 *
 * Returns `null` when the table has no pathBuilder.
 */
export function getPathBuilderLinkResolver<TData>(
  table: Table<TData>,
): LinkResolver<TData> | null {
  const pathBuilder = table.options.meta?.pathBuilder;
  if (!pathBuilder) return null;

  const linkColumnId = findLinkColumnId(table);
  if (!linkColumnId) return null;

  return (row, colId) =>
    colId === linkColumnId ? pathBuilder(row.original) : null;
}
